import { useSelector } from 'react-redux';
import type { RootState } from 'src/redux/store';
import { ADDON_PERMISSIONS, PERMISSION_MATRIX } from './permissions.config';
import type { AccountStatus, Addon, Permission, Plan, Role } from './types';

export interface UsePermissionsReturn {
  /** Check if the current user can access a permission (plan + add-ons considered). */
  can: (permission: Permission) => boolean;
  /** True only when the user can access every listed permission. */
  canAll: (permissions: Permission[]) => boolean;
  /** True when the user can access at least one of the listed permissions. */
  canAny: (permissions: Permission[]) => boolean;
  /** Resolved account status — checked before any feature permission. */
  accountStatus: AccountStatus;
  /** The user's resolved plan (localseo users always resolve to 'localseo'). */
  plan: Plan;
  /** The user's role within their account. */
  role: Role;
  /** Add-ons the user has actively purchased. */
  activeAddons: Addon[];
  /** True once auth + subscription data has finished loading. */
  isReady: boolean;
}

/**
 * Primary hook for all access control decisions.
 *
 * @example
 * const { can } = usePermissions();
 * if (can('leads.form_builder')) { ... }
 *
 * @example
 * const { canAny } = usePermissions();
 * const showLocalMenu = canAny(['local.hub', 'local.business_profile']);
 */
export const usePermissions = (): UsePermissionsReturn => {
  const activePlan = useSelector((state: RootState) => state.user.activePlan);
  const subscription = useSelector((state: RootState) => state.user.subscription);
  const premiumUser = useSelector((state: RootState) => state.user.premiumUser);
  const userDetails = useSelector((state: RootState) => state.counter.userDetails) as any;
  const stripeLoader = useSelector((state: RootState) => state.siteAudit.stripeLoader) ?? true;
  const searchesDetails: any = useSelector((state: RootState) => state.keywordResearch.searchesDetails) ?? null;
  const loader = useSelector((state: RootState) => state.siteAudit.loader) ?? true;

  // ── Add-on state from Redux ───────────────────────────────────────────────
  // isAiAddonPurchased: set after a successful buyAiAddon call.
  const isAiAddonPurchased = useSelector(
    (state: RootState) => {
      return state.dashboard.isAiAddonPurchased}
  ) || (searchesDetails?.totalAiSlots && searchesDetails?.totalAiSlots !== 0) ||
    false; 

  // isLocalSEOAddonBuy: set when user purchases the Local SEO add-on.
  const isLocalSEOAddonBuy = useSelector(
    (state: RootState) => state.counter.isLocalSEOAddonBuy
  );
  const stripeSubscriptionSlots = useSelector(
    (state: RootState) => (state.user.subscription as any)?.plan_name
  );

  // Derive which feature-unlock add-ons the user has active.
  // This list is used as a fallback inside can() when the base plan denies access.
  const activeAddons: Addon[] = [];
  if (isAiAddonPurchased) activeAddons.push('addon.ai_visibility');
  // isLocalSEOAddonBuy is set when the user purchases the Local SEO add-on
  // via UpgradeAgencyPlanModal (isLocalSeoUnlock / isMapRankingUnlock flow).
  // It unlocks the Local SEO hub and Map Rank Tracker for diy/designer plans.
  if (isLocalSEOAddonBuy) activeAddons.push('addon.localseo');
  // Backlinks add-on: signalled by a 'backlinks' flag in the subscription slots.
  if (stripeSubscriptionSlots?.backlinks) activeAddons.push('addon.backlinks');

  const isReady = !stripeLoader && !loader && !!userDetails?.email;

  // LocalSEO is a separate user type — treat it as its own plan so the
  // matrix can list it explicitly rather than duplicating flag checks.
  const isLocalSeo = !!userDetails?.isLocalSeo;
  let resolvedPlan = activePlan?.toLowerCase();
  if (resolvedPlan === 'starter' || resolvedPlan === 'starterplus' || resolvedPlan === 'starter plus') {
    resolvedPlan = 'diy';
  } else if (resolvedPlan === 'pro') {
    resolvedPlan = 'designer';
  } else if (resolvedPlan === 'proplus' || resolvedPlan === 'pro plus') {
    resolvedPlan = 'proplus';
  } else if (resolvedPlan === 'expert') {
    resolvedPlan = 'agency';
  } else if (resolvedPlan === 'expertplus' || resolvedPlan === 'expert plus') {
    resolvedPlan = 'expertplus';
  }

  const plan: Plan = isLocalSeo ? 'localseo' : ((resolvedPlan as Plan) || 'free');

  // Role — members are restricted from admin-only routes regardless of plan.
  const role: Role = userDetails?.role_type === 'member' ? 'member' : 'admin';

  // ── Account status ────────────────────────────────────────────────────────
  // Resolved once here; individual can() calls don't need to re-derive it.
  const hasSubscriptionId = !!subscription?.subscription_id;
  const isPaused = !!subscription?.pause_collection?.behavior;
  const isTemp = userDetails?.temp === true;

  let accountStatus: AccountStatus = 'active';
  if (isReady) {
    if (isPaused) {
      accountStatus = 'paused';
    } else if (!hasSubscriptionId && !premiumUser && isTemp) {
      accountStatus = 'no_plan';
    } else if (!hasSubscriptionId && !premiumUser && !isTemp && plan !== 'free') {
      accountStatus = 'expired';
    }
  }

  // ── Core permission check ─────────────────────────────────────────────────
  const can = (permission: Permission): boolean => {
    // Degraded accounts can only reach billing and settings.
    if (
      accountStatus === 'paused' ||
      accountStatus === 'expired' ||
      accountStatus === 'no_plan'
    ) {
      return permission === 'account.billing' || permission === 'account.settings';
    }

    // Role gate — members cannot access billing or user management.
    if (role === 'member') {
      if (permission === 'account.billing' || permission === 'team.user_management') {
        return false;
      }
    }

    // ── Pass 1: base plan ──────────────────────────────────────────────────
    const allowedPlans = PERMISSION_MATRIX[permission];
    if (!allowedPlans) return false;
    if (allowedPlans.includes(plan)) return true;

    // ── Pass 2: purchased add-ons ─────────────────────────────────────────
    // If the base plan doesn't grant access, check whether the user has
    // purchased an add-on that unlocks this specific permission.
    const unlockedBy = ADDON_PERMISSIONS[permission];
    if (!unlockedBy) return false;
    return unlockedBy.some((addon) => activeAddons.includes(addon));
  };

  return {
    can,
    canAll: (permissions) => permissions.every(can),
    canAny: (permissions) => permissions.some(can),
    accountStatus,
    plan,
    role,
    activeAddons,
    isReady,
  };
};
